import React, { useState } from 'react';
import { Tooltip, message, Radio, Popover } from 'antd';
import dayjs from 'dayjs';
import { useTaskDrawer } from '../TaskDrawerContext';
import { formatHHMMSS } from '../utils/timeFormatters';
import MilestoneCompletionModal from './MilestoneCompletionModal';
import { updateTaskStatus } from '../../api/projectManagementApi';

export default function TaskHeader() {
    const {
        task,
        drawerStack,
        popTo,
        pushTask,
        displayActualTimeMs,
        editTitle,
        setEditTitle,
        isOverrun,
        isCompleted,
        statusUpdating,
        attendance,
        activeAttendanceTask,
        isTimerRunning,
        shareUrl,
        sharingLoading,
        handleCompleteTask,
        handleShare,
        handleRevokeShare,
        copyInternalLink,
        handleStartTimer,
        handleStopTimer,
        mode,
        itemType,
        setItemType,
        onTaskUpdated,
    } = useTaskDrawer();

    const [completionModalOpen, setCompletionModalOpen] = useState(false);
    const [completingLoading, setCompletingLoading] = useState(false);

    const isMilestone = task?.itemType === 'milestone' || itemType === 'milestone';

    const onCompleteButtonClick = () => {
        if (isMilestone) {
            setCompletionModalOpen(true);
        } else {
            handleCompleteTask();
        }
    };

    const handleConfirmMilestoneCompletion = async (comments, verifiedChecklist) => {
        setCompletingLoading(true);
        try {
            const extra = verifiedChecklist ? { deliverableChecklist: verifiedChecklist } : {};
            const { data } = await updateTaskStatus(task._id, 'Completed', '', comments, extra);
            message.success('Milestone completed and synced to CRE successfully!');
            setCompletionModalOpen(false);
            if (onTaskUpdated) onTaskUpdated(data?.result);
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to complete milestone.');
        } finally {
            setCompletingLoading(false);
        }
    };

    const isLocalhost = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
    );

    const internalLink = task ? `${window.location.origin}/project-management?task=${task.taskId || task._id}` : '';

    const sharePopoverContent = (
        <div className="w-80 p-2 space-y-4">
            {/* Internal Team Link */}
            <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-100">
                    <span className="material-symbols-outlined text-sm text-[#03488a] dark:text-[#a7c8ff]">lock</span>
                    <span>Internal Team Link</span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Opens the task drawer for signed-in team members.
                </p>
                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
                    <input
                        type="text"
                        readOnly
                        value={internalLink}
                        onClick={(e) => e.target.select()}
                        className="bg-transparent text-xs text-slate-700 dark:text-slate-300 px-1 w-full focus:outline-none truncate font-mono"
                    />
                    <button
                        onClick={copyInternalLink}
                        className="px-2.5 py-1 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900 text-[#03488a] dark:text-[#a7c8ff] rounded text-xs font-bold transition-colors whitespace-nowrap cursor-pointer"
                    >
                        Copy
                    </button>
                </div>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-800" />

            {/* Public Guest Link */}
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-100">
                        <span className="material-symbols-outlined text-sm text-emerald-600 dark:text-emerald-400">public</span>
                        <span>Public Guest Link</span>
                    </div>
                    {shareUrl && (
                        <span className="text-[10px] bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-800 font-semibold">
                            Active (7-day)
                        </span>
                    )}
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Sanitized view for external clients. No login required.
                </p>

                {shareUrl ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
                            <input
                                type="text"
                                readOnly
                                value={shareUrl}
                                onClick={(e) => e.target.select()}
                                className="bg-transparent text-xs text-slate-700 dark:text-slate-300 px-1 w-full focus:outline-none truncate font-mono"
                            />
                            <button
                                onClick={() => {
                                    navigator.clipboard?.writeText?.(shareUrl);
                                    message.success('Public guest link copied!');
                                }}
                                className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900 text-emerald-700 dark:text-emerald-300 rounded text-xs font-bold transition-colors whitespace-nowrap cursor-pointer"
                            >
                                Copy
                            </button>
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={handleRevokeShare}
                                disabled={sharingLoading}
                                className="text-[11px] text-rose-600 hover:text-rose-700 dark:text-rose-400 font-semibold flex items-center gap-1 hover:underline cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-xs">link_off</span>
                                Revoke guest link
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={handleShare}
                        disabled={sharingLoading}
                        className="w-full py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50"
                    >
                        <span className="material-symbols-outlined text-sm">add_link</span>
                        {sharingLoading ? 'Generating...' : 'Generate Guest Link'}
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <header className="border-b border-[#e7e8e9] dark:border-[#303030] bg-white dark:bg-[#1f1f1f] px-6 py-4">
            <div className="flex justify-between items-start mb-4 gap-4">
                <div className="space-y-1 flex-1">
                    {drawerStack && drawerStack.length > 1 ? (
                        <div className="flex items-center flex-wrap gap-1.5 text-xs text-ed-on-surface-variant font-bold mb-2">
                            {drawerStack.map((item, idx) => {
                                const isLast = idx === drawerStack.length - 1;
                                const isItemMilestone = item.itemType === 'milestone';
                                return (
                                    <React.Fragment key={item.id || idx}>
                                        <button
                                            onClick={() => !isLast && popTo(idx)}
                                            disabled={isLast}
                                            className={`flex items-center gap-1 transition-colors px-2 py-0.5 rounded-md ${
                                                isLast
                                                    ? 'text-[#03488a] dark:text-[#a7c8ff] bg-blue-50 dark:bg-blue-950/60 cursor-default'
                                                    : 'hover:text-[#03488a] hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-slate-600 dark:text-slate-400'
                                            }`}
                                        >
                                            <span className={isItemMilestone ? 'text-amber-500 font-black' : 'text-slate-400 font-black'}>
                                                {isItemMilestone ? '◆' : '○'}
                                            </span>
                                            <span className="truncate max-w-[180px]">{item.title || `Item #${String(item.id).slice(-6)}`}</span>
                                        </button>
                                        {!isLast && <span className="material-symbols-outlined text-sm text-slate-400">chevron_right</span>}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    ) : (task?.parentId && typeof task.parentId === 'object') ? (
                        <div className="flex items-center flex-wrap gap-1.5 text-xs text-ed-on-surface-variant font-bold mb-2">
                            <button
                                onClick={() => {
                                    const pId = task.parentId?._id || task.parentId;
                                    const pTitle = task.parentId?.title || 'Parent Task';
                                    if (pId && pushTask) {
                                        pushTask(pId, 'view', pTitle, null);
                                    }
                                }}
                                className="flex items-center gap-1 text-slate-600 dark:text-slate-400 hover:text-[#03488a] dark:hover:text-[#a7c8ff] hover:bg-slate-100 dark:hover:bg-slate-800 px-2 py-0.5 rounded-md transition-colors cursor-pointer"
                                title="Open Parent Task"
                            >
                                <span className={task.parentId?.itemType === 'milestone' ? 'text-amber-500 font-black' : 'text-slate-400 font-black'}>
                                    {task.parentId?.itemType === 'milestone' ? '◆' : '○'}
                                </span>
                                <span className="truncate max-w-[180px]">{task.parentId.title || task.parentId.taskId || 'Parent Task'}</span>
                            </button>
                            <span className="material-symbols-outlined text-sm text-slate-400">chevron_right</span>
                            <span className="flex items-center gap-1 text-[#03488a] dark:text-[#a7c8ff] bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded-md cursor-default">
                                <span className={isMilestone ? 'text-amber-500 font-black' : 'text-slate-400 font-black'}>
                                    {isMilestone ? '◆' : '○'}
                                </span>
                                <span className="truncate max-w-[180px]">{task?.title || `Item #${String(task?._id).slice(-6)}`}</span>
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-ed-on-surface-variant font-label text-sm">
                            <span className="font-bold text-primary dark:text-slate-300 text-base">#{task?.taskNum || task?.taskId || task?._id?.slice(-6)}</span>
                        </div>
                    )}
                    <input
                        type="text"
                        value={editTitle}
                        onChange={(event) => setEditTitle(event.target.value)}
                        placeholder={isMilestone ? "Milestone Title" : "Task Title"}
                        autoFocus={mode === 'create'}
                        className="w-full text-2xl font-headline font-extrabold text-[#191c1d] dark:text-white tracking-tight bg-transparent border-b border-transparent hover:border-[#e7e8e9] dark:hover:border-[#303030] focus:border-primary dark:focus:border-slate-500 focus:outline-none py-0.5 transition-colors"
                    />
                </div>

                {mode !== 'create' && !isMilestone && (
                    <div className="flex items-center gap-4 bg-[#f8f9fa] dark:bg-[#141414] px-5 py-3 rounded-xl border border-[#e7e8e9] dark:border-[#303030] shrink-0 ml-auto min-w-[200px] justify-end">
                        {isTimerRunning ? (
                            <button
                                onClick={() => handleStopTimer()}
                                className="w-10 h-10 bg-[#ffdad6] text-[#93000a] flex items-center justify-center hover:bg-[#ba1a1a] hover:text-white transition-colors shadow-sm shrink-0"
                                style={{ borderRadius: '50%' }}
                                title="Stop Tracking"
                            >
                                <span className="material-symbols-outlined text-xl">stop</span>
                            </button>
                        ) : (
                            <button
                                onClick={handleStartTimer}
                                disabled={isCompleted}
                                className={`w-10 h-10 flex items-center justify-center transition-colors shadow-sm shrink-0 ${isCompleted ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 opacity-50 cursor-not-allowed' : 'bg-[#c4eed0] text-[#0f5223] hover:bg-[#146c2e] hover:text-white'}`}
                                style={{ borderRadius: '50%' }}
                                title="Start Tracking"
                            >
                                <span className="material-symbols-outlined text-xl">play_arrow</span>
                            </button>
                        )}
                        <div className="flex flex-col items-end">
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Logged Today</span>
                            <div className="text-2xl font-headline font-black tracking-tighter text-[#191c1d] dark:text-white flex items-center">
                                <span className={isOverrun ? 'text-error animate-pulse' : ''}>{formatHHMMSS(displayActualTimeMs)}</span>
                                <span className="text-slate-400 font-medium text-sm ml-1.5">/ {task?.estimatedTime || '04:30:00'}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {mode === 'create' ? (
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Work Item Type:</span>
                        <Radio.Group
                            value={itemType}
                            onChange={(e) => setItemType(e.target.value)}
                            buttonStyle="solid"
                            size="small"
                        >
                            <Radio.Button value="task">○ Task</Radio.Button>
                            <Radio.Button value="milestone">◆ Milestone</Radio.Button>
                        </Radio.Group>
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex gap-2 items-center">
                        {isMilestone ? (
                            <span className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300 dark:border-amber-800 text-xs font-black uppercase tracking-wider flex items-center gap-1">
                                <span className="text-amber-500 font-bold">◆</span> MILESTONE
                            </span>
                        ) : (
                            <span className="px-3 py-1.5 rounded-full bg-[#ffdad6] text-[#93000a] dark:bg-rose-950/60 dark:text-rose-300 dark:border dark:border-rose-800/50 text-xs font-black uppercase tracking-wider flex items-center gap-0.5">
                                <span className="material-symbols-outlined font-black" style={{ fontSize: '13px' }}>priority_high</span> {task?.priority || 'HIGH'}
                            </span>
                        )}
                        <span className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider flex items-center gap-0.5 ${
                            task?.status === 'Incomplete'
                                ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-200 border border-rose-300 dark:border-rose-800 shadow-xs'
                                : 'bg-[#c9e6ff] text-[#004c6e] dark:bg-slate-800 dark:text-slate-200 dark:border dark:border-slate-700'
                        }`}>
                            <span className="material-symbols-outlined font-bold" style={{ fontSize: '13px' }}>
                                {task?.status === 'Incomplete' ? 'error' : 'potted_plant'}
                            </span> {task?.status || 'IN PROGRESS'}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={onCompleteButtonClick}
                            disabled={isCompleted || statusUpdating}
                            className={`px-4 py-1.5 rounded-lg font-label font-bold text-sm flex items-center gap-2 shadow-sm transition-all active:scale-95 bg-gradient-primary dark:bg-[#262626] dark:border dark:border-[#303030] text-white dark:hover:bg-[#303030] ${isCompleted || statusUpdating ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-90 cursor-pointer'}`}
                        >
                            <span className="material-symbols-outlined text-sm font-bold">check_circle</span>
                            {isCompleted ? (isMilestone ? 'Milestone Completed' : 'Task Completed') : (isMilestone ? 'Complete Milestone' : 'Complete Task')}
                        </button>

                        {isLocalhost && (
                            <Popover
                                content={sharePopoverContent}
                                title={<span className="font-bold text-xs text-slate-800 dark:text-slate-100">Share Task</span>}
                                trigger="click"
                                placement="bottomRight"
                            >
                                <button
                                    className="bg-ed-surface-container-high hover:bg-ed-surface-container-highest text-ed-on-surface px-3 py-1.5 rounded-lg font-label font-bold text-sm flex items-center gap-1.5 transition-colors cursor-pointer"
                                    title="Share Task"
                                >
                                    <span className="material-symbols-outlined text-sm font-bold">share</span>
                                    <span>Share</span>
                                    {shareUrl && (
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" title="Public link active" />
                                    )}
                                </button>
                            </Popover>
                        )}
                    </div>
                </div>
            )}

            {task?.rejectionNote && isMilestone && !isCompleted && (
                <div className="mt-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-lg p-2.5 text-xs flex items-start gap-2">
                    <span className="text-rose-600 font-bold text-sm">⚠️</span>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-rose-700 dark:text-rose-300">
                                Client Rejected Milestone {task.revision ? `(Revision ${task.revision})` : ''}:
                            </span>
                            <span className="text-[10px] text-rose-500 font-medium">
                                {task.rejectedBy ? `by ${task.rejectedBy}` : ''} {task.rejectedAt ? `on ${dayjs(task.rejectedAt).format('MMM D, YYYY h:mm A')}` : ''}
                            </span>
                        </div>
                        <p className="text-rose-900 dark:text-rose-200 mt-0.5 font-medium">{task.rejectionNote}</p>
                    </div>
                </div>
            )}

            <MilestoneCompletionModal
                open={completionModalOpen}
                onClose={() => setCompletionModalOpen(false)}
                onConfirm={handleConfirmMilestoneCompletion}
                loading={completingLoading}
                milestone={task || {}}
            />
        </header>
    );
}
